import type { Recipe } from '@/api/types'
import { NutritionalValueCalculator } from '@/features/NutritionFacts'

function markdownToHtml(md: string): string {
  if (!md) return ''

  const lines = md.split('\n')
  const output: string[] = []
  let inOl = false
  let inUl = false

  const closeList = () => {
    if (inOl) {
      output.push('</ol>')
      inOl = false
    }
    if (inUl) {
      output.push('</ul>')
      inUl = false
    }
  }

  for (const raw of lines) {
    const line = raw
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/_(.+?)_/g, '<em>$1</em>')

    if (/^#{1,6}\s/.test(line)) {
      closeList()
      const level = line.match(/^(#+)/)?.[1].length ?? 2
      const text = line.replace(/^#+\s/, '')
      output.push(`<h${level}>${text}</h${level}>`)
    } else if (/^\d+\.\s/.test(line)) {
      if (inUl) {
        output.push('</ul>')
        inUl = false
      }
      if (!inOl) {
        output.push('<ol>')
        inOl = true
      }
      output.push(`<li>${line.replace(/^\d+\.\s/, '')}</li>`)
    } else if (/^-\s/.test(line)) {
      if (inOl) {
        output.push('</ol>')
        inOl = false
      }
      if (!inUl) {
        output.push('<ul>')
        inUl = true
      }
      output.push(`<li>${line.replace(/^-\s/, '')}</li>`)
    } else if (line.trim() === '') {
      closeList()
      output.push('<br>')
    } else {
      closeList()
      output.push(`<p>${line}</p>`)
    }
  }

  closeList()
  return output.join('\n')
}

function fmt(value: number | undefined, decimals = 1): string {
  if (value === undefined || isNaN(value)) return 'N/A'
  return value.toFixed(decimals)
}

function tagPills(names: string[]): string {
  if (!names.length) return '—'
  return names.map((n) => `<span class="tag">${n}</span>`).join(' ')
}

function recipeSection(recipe: Recipe): string {
  const { name, idRecipe, preparationDetails, recipeIngredients, tags, cuisine, expertiseLevel } =
    recipe

  const mealTypes = tags.filter((t) => t.category === 'MEAL_TYPE').map((t) => t.name)
  const seasons = tags.filter((t) => t.category === 'SEASON').map((t) => t.name)
  const cookingMethods = tags.filter((t) => t.category === 'COOKING_METHOD').map((t) => t.name)
  const storage = tags.filter((t) => t.category === 'STORAGE').map((t) => t.name)

  const nutrition = NutritionalValueCalculator.fromRecipeIngredients(
    recipeIngredients,
    preparationDetails?.servings,
  )

  const ingredientsRows = recipeIngredients
    .map((ri) => {
      const unit = ri.unit?.name ?? 'g'
      const qty = ri.quantity != null ? `${fmt(ri.quantity, 0)} ${unit}` : '—'
      const ingName = ri.ingredient.name ?? ri.ingredient.englishName ?? ''
      return `<tr><td>${qty}</td><td>${ingName}</td></tr>`
    })
    .join('\n')

  const prepSteps = preparationDetails?.preparationSteps
    ? markdownToHtml(preparationDetails.preparationSteps)
    : '<p>—</p>'

  const metaParts = [
    preparationDetails?.servings
      ? `<span>Servings: <b>${preparationDetails.servings}</b></span>`
      : '',
    preparationDetails?.preparationTime
      ? `<span>Prep: <b>${preparationDetails.preparationTime} min</b></span>`
      : '',
    preparationDetails?.cookingTime
      ? `<span>Cook: <b>${preparationDetails.cookingTime} min</b></span>`
      : '',
    cuisine ? `<span>Cuisine: <b>${cuisine}</b></span>` : '',
    expertiseLevel ? `<span>Level: <b>${expertiseLevel}</b></span>` : '',
  ]
    .filter(Boolean)
    .join(' · ')

  return `
<section id="recipe-${idRecipe}">
  <h2>${name}</h2>
  <div class="meta">${metaParts || '—'}</div>
  <div class="tags">
    ${mealTypes.length ? `<div><b>Meal type:</b> ${tagPills(mealTypes)}</div>` : ''}
    ${seasons.length ? `<div><b>Season:</b> ${tagPills(seasons)}</div>` : ''}
    ${storage.length ? `<div><b>Storage:</b> ${tagPills(storage)}</div>` : ''}
    ${cookingMethods.length ? `<div><b>Cooking:</b> ${tagPills(cookingMethods)}</div>` : ''}
  </div>

  <h3>Ingredients</h3>
  <table>
    <thead><tr><th>Qty</th><th>Ingredient</th></tr></thead>
    <tbody>${ingredientsRows}</tbody>
  </table>

  <h3>Preparation</h3>
  <div class="prep">${prepSteps}</div>

  ${recipe.otherInfo ? `<h3>Notes</h3><p>${recipe.otherInfo}</p>` : ''}

  <h3>Nutrition <small>(per serving)</small></h3>
  <table class="nutrition">
    <tbody>
      <tr><td>Energy</td><td>${fmt(nutrition?.energy)} kcal</td></tr>
      <tr><td>Protein</td><td>${fmt(nutrition?.protein)} g</td></tr>
      <tr><td>Carbohydrates</td><td>${fmt(nutrition?.carbohydrate)} g</td></tr>
      <tr><td>Fat</td><td>${fmt(nutrition?.fat)} g</td></tr>
      <tr><td>Fiber</td><td>${fmt(nutrition?.fiber)} g</td></tr>
    </tbody>
  </table>

  <a class="back" href="#toc">↑ Back to top</a>
</section>`
}

export function generateRecipesHtml(recipes: Recipe[]): string {
  const date = new Date().toLocaleString()

  const toc = recipes
    .map((r) => `<li><a href="#recipe-${r.idRecipe}">${r.name}</a></li>`)
    .join('\n')

  const sections = recipes.map(recipeSection).join('\n<hr>\n')

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Recipes</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, sans-serif; margin: 0; padding: 24px; color: #1a1a1a; line-height: 1.5; max-width: 900px; margin: 0 auto; }
  h1 { font-size: 2rem; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; }
  h2 { font-size: 1.4rem; margin-top: 32px; color: #111827; }
  h3 { font-size: 1rem; margin-top: 20px; color: #374151; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
  nav { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px 24px; margin: 16px 0; }
  nav ul { columns: 3; column-gap: 24px; padding: 0; margin: 0; list-style: none; }
  nav li { padding: 2px 0; break-inside: avoid; }
  nav a { color: #2563eb; text-decoration: none; font-size: 0.9rem; }
  nav a:hover { text-decoration: underline; }
  section { padding: 16px 0; }
  .meta { color: #6b7280; font-size: 0.9rem; margin: 6px 0 12px; }
  .tags { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; font-size: 0.85rem; }
  .tag { display: inline-block; background: #eff6ff; color: #1d4ed8; border-radius: 4px; padding: 1px 7px; font-size: 0.8rem; }
  table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 0.9rem; }
  th { text-align: left; background: #f3f4f6; padding: 6px 10px; border: 1px solid #e5e7eb; }
  td { padding: 5px 10px; border: 1px solid #e5e7eb; }
  tr:nth-child(even) td { background: #fafafa; }
  table.nutrition { max-width: 280px; }
  table.nutrition td:first-child { font-weight: 500; }
  table.nutrition td:last-child { text-align: right; }
  .prep { background: #fafafa; border-left: 3px solid #e5e7eb; padding: 12px 16px; border-radius: 0 6px 6px 0; }
  .prep h2, .prep h3 { font-size: 1rem; margin: 12px 0 4px; }
  .prep ol, .prep ul { padding-left: 20px; margin: 4px 0; }
  .prep li { margin: 3px 0; }
  .prep p { margin: 4px 0; }
  .back { display: inline-block; margin-top: 16px; font-size: 0.85rem; color: #6b7280; text-decoration: none; }
  .back:hover { color: #2563eb; }
  hr { border: none; border-top: 1px solid #e5e7eb; margin: 32px 0; }
  small { font-weight: normal; color: #9ca3af; }
</style>
</head>
<body>
<h1>Recipes</h1>
<p style="color:#6b7280;font-size:0.85rem">Generated on ${date} · ${recipes.length} published recipes</p>

<nav id="toc">
  <ul>
    ${toc}
  </ul>
</nav>

${sections}
</body>
</html>`
}
